import { useState, useRef, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import ArrowRightSwipeSvg from '~/@ui/_sharedSvgs/ui/ArrowRightSwipe.svg?react'

interface SwipeToConfirmProps {
  lightTheme?: boolean
  label?: string
  onConfirm?: () => Promise<void>
  threshold?: number
  sliderSize?: number
  processingDuration?: number
  className?: string
  resetTrigger?: number // Add reset trigger prop
}

const SwipeToConfirm: React.FC<SwipeToConfirmProps> = ({
  lightTheme,
  label = 'Vyzerá to dobre',
  onConfirm,
  threshold = 0.8,
  processingDuration = 1000,
  className = '',
  resetTrigger = 0, // Add reset trigger prop
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [dragOffset, setDragOffset] = useState<number>(0)
  const [startX, setStartX] = useState<number>(0)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<HTMLDivElement>(null)

  const THRESHOLD = threshold
  const SLIDER_WIDTH = 68
  const SLIDER_HEIGHT = 48
  const SLIDER_RADIUS = 24
  const TRACK_HEIGHT = 56
  const TRACK_RADIUS = 28
  const PADDING = (TRACK_HEIGHT - SLIDER_HEIGHT) / 2 // 4px padding pre centrované umiestnenie
  const CONTAINER_HEIGHT = TRACK_HEIGHT

  // Reset when resetTrigger changes
  useEffect(() => {
    if (resetTrigger > 0) {
      setIsProcessing(false)
      setIsDragging(false)
      setDragOffset(0)
    }
  }, [resetTrigger])

  // Simulate order processing
  const processOrder = async (): Promise<void> => {
    setIsProcessing(true)

    try {
      // Simulate API call delay
      await new Promise<void>((resolve) =>
        setTimeout(resolve, processingDuration)
      )

      // Call custom onConfirm callback if provided
      if (onConfirm) {
        await onConfirm()
      }

      setIsProcessing(false)
    } catch (error) {
      console.error('Error processing order:', error)
      setIsProcessing(false)
      setDragOffset(0) // Reset on error
    }
  }

  const handleStart = (clientX: number): void => {
    if (isProcessing) return
    setIsDragging(true)
    setStartX(clientX)
  }

  const handleMove = (clientX: number): void => {
    if (!isDragging || isProcessing) return

    const container = containerRef.current
    if (!container) return

    const containerWidth: number = container.offsetWidth
    const maxOffset: number = containerWidth - SLIDER_WIDTH - PADDING * 2
    const offset: number = Math.max(0, Math.min(maxOffset, clientX - startX))

    setDragOffset(offset)
  }

  const handleEnd = (): void => {
    if (!isDragging || isProcessing) return

    const container = containerRef.current
    if (!container) return

    const containerWidth: number = container.offsetWidth
    const maxOffset: number = containerWidth - SLIDER_WIDTH - PADDING * 2
    const progress: number = dragOffset / maxOffset

    if (progress >= THRESHOLD) {
      // Keep at full width and start processing
      setDragOffset(maxOffset)
      processOrder()
    } else {
      setDragOffset(0)
    }

    setIsDragging(false)
  }

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault() // Prevent text selection and other mouse defaults
    handleStart(e.clientX)
  }

  const handleMouseMove = (e: MouseEvent): void => {
    handleMove(e.clientX)
  }

  const handleMouseUp = (): void => {
    handleEnd()
  }

  // Touch events - improved handling
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
    // Only prevent default on touchstart to ensure we can control the gesture
    e.preventDefault()
    handleStart(e.touches[0].clientX)
  }

  const handleTouchMove = (e: TouchEvent): void => {
    // Only prevent default if we're actively dragging and the event is cancelable
    if (isDragging && e.cancelable) {
      e.preventDefault()
    }
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX)
    }
  }

  const handleTouchEnd = (): void => {
    handleEnd()
  }

  useEffect(() => {
    if (isDragging) {
      // Add mouse event listeners
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      // Add touch event listeners with improved options
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false, // Allow preventDefault when needed
      })
      document.addEventListener('touchend', handleTouchEnd, {
        passive: true, // touchend doesn't need preventDefault
      })

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, startX, dragOffset])

  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        // Prevent text selection during drag
        userSelect: 'none',
        // Improve touch handling
        touchAction: 'none',
      }}>
      {/* Swipe to Confirm */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          ref={containerRef}
          sx={(theme) => ({
            position: 'relative',
            height: CONTAINER_HEIGHT,
            borderRadius: `${TRACK_RADIUS}px`,
            bgcolor: lightTheme
              ? theme.palette.custom.zGrey200
              : theme.palette.custom.zGrey300,
            overflow: 'hidden',
            // Prevent scrolling within this component
            touchAction: 'none',
          })}>
          {/* Stretching Green Pill */}
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: PADDING,
              left: PADDING,
              height: SLIDER_HEIGHT,
              borderRadius: `${SLIDER_RADIUS}px`,
              bgcolor: theme.palette.custom.zGreen300,
              width: `${SLIDER_WIDTH + dragOffset}px`,
              transition: isDragging
                ? 'none'
                : 'width 0.3s ease-out, background-color 0.3s ease-out',
            })}
          />

          {/* Background Text - hidden when green pill covers it */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 10,
              clipPath: containerRef.current
                ? `inset(0 0 0 ${Math.max(0, SLIDER_WIDTH + dragOffset - 20)}px)`
                : 'none',
            }}>
            <Typography className="text-base font-medium text-zGrey400">
              {label}
            </Typography>
          </Box>

          {/* Dynamic arrows/text positioned in the middle of the green pill */}
          <Box
            sx={{
              position: 'absolute',
              top: PADDING,
              left: PADDING,
              height: SLIDER_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 30,
              width: `${SLIDER_WIDTH + dragOffset}px`,
              transition: isDragging ? 'width 0ms' : 'width 0.3s ease-out',
            }}>
            {isProcessing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: 'white', fontWeight: 500 }}>
                  spracovávam...
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  right: '23px',
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center',
                  gap: 0.5,
                }}>
                <ArrowRightSwipeSvg />
                <ArrowRightSwipeSvg />
              </Box>
            )}
          </Box>

          {/* Slider Handle */}
          <Box
            ref={sliderRef}
            sx={(theme) => ({
              position: 'absolute',
              top: PADDING,
              left: `${PADDING + dragOffset}px`,
              width: SLIDER_WIDTH,
              height: SLIDER_HEIGHT,
              borderRadius: `${SLIDER_RADIUS}px`,
              boxShadow: 3,
              transition: isDragging
                ? 'left 0ms'
                : 'left 0.3s ease-out, background-color 0.3s ease-out',
              zIndex: 20,
              bgcolor: theme.palette.custom.zGreen300,
              cursor: isProcessing
                ? 'default'
                : isDragging
                  ? 'grabbing'
                  : 'grab',
              userSelect: 'none',
              touchAction: 'none',
            })}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default SwipeToConfirm
